import type { Allocation, PlanNode, ProjectDocument } from '@timelog/pm-shared';

import { applyContractPrefill, subtreeIds } from './contractPrefill';
import { adoptServerCodes } from './saveMerge';
import {
  duplicateSubtrees,
  indentNode,
  insertChildWithTransfer,
  insertNode,
  moveSubtree,
  outdentNode,
  removeSubtrees,
  updateManyNodeFields,
  updateNodeFields,
} from './treeOps';
import type { InsertPosition } from './treeOps';

/**
 * The Planning editor's client state (ADR 0010): one reducer owns the
 * document while the page is mounted. Inline edits buffer inside the cell
 * components and only COMMIT here — so typing never re-renders the tree and
 * the rollup recomputes once per commit, not per keystroke.
 */

/**
 * `title`/`hours`/`start`/`target` are row cells (the start date since 3 Nov,
 * when the side panel's Start – end line went). `end` has no cell: it moves
 * with the target (ADR 0051) and is dragged in the timeline.
 */
export type EditField = 'title' | 'hours' | 'target' | 'start' | 'end';

export type SaveStatus = 'saved' | 'dirty' | 'saving' | 'error' | 'conflict';

export type EditorState = {
  projectId: string;
  /** Authoritative while editing; server state is only adopted explicitly. */
  document: ProjectDocument;
  /** Bumped on every document mutation; drives the autosave debounce. */
  revision: number;
  /** The version the next PUT must carry (BR-20). */
  serverVersion: number;
  /** Last revision confirmed saved. */
  savedRevision: number;
  saveStatus: SaveStatus;
  foldedIds: ReadonlySet<string>;
  activeId: string | null;
  /**
   * The one popover-sized editor (v5: "one thing open at a time"): the node
   * whose Resources popover is open. Row menus (status, kebab) are plain MUI
   * menus with a backdrop, so they need no reducer slot.
   */
  resourcesNodeId: string | null;
  selectedIds: ReadonlySet<string>;
  selectionAnchorId: string | null;
  /**
   * `openPicker` records that editing was entered by a POINTER on a cell whose
   * editor has a panel (the date cell's calendar), so the panel can open with
   * the field. It has to live in state: the cell remounts when editing starts,
   * so a ref set by the click is gone by the time the editor's mount effect
   * runs — which is why the old ref-based auto-open silently never fired.
   */
  editing: { nodeId: string; field: EditField; openPicker?: boolean } | null;
  /**
   * The contract a newly created task is stamped with (ADR 0033: the project
   * default is a creation-time prefill, never a read-time fallback — an
   * unmapped task has no contract and no price).
   *
   * It lives in state rather than being read off the document because the
   * fallback needs the contract LIST: when a project holds exactly one
   * contract and no default has been chosen, that contract is the only
   * sensible answer, and making the user pick it on every task was the pilot's
   * complaint. The provider owns the value; `SET_CONTRACT_PREFILL` carries it.
   */
  contractPrefillId: string | undefined;
};

export type NodeFieldsPatch = Partial<Omit<PlanNode, 'id' | 'children'>>;

export type EditorAction =
  | { type: 'RESET_FROM_SERVER'; projectId: string; document: ProjectDocument; version: number }
  | { type: 'TOGGLE_FOLD'; id: string }
  | { type: 'SET_ACTIVE'; id: string | null }
  | { type: 'OPEN_RESOURCES'; id: string }
  | { type: 'CLOSE_RESOURCES' }
  | { type: 'START_EDITING'; nodeId: string; field: EditField; openPicker?: boolean }
  | { type: 'STOP_EDITING' }
  | { type: 'SET_PROJECT_NAME'; name: string }
  | { type: 'SET_NODE_FIELDS'; nodeId: string; patch: NodeFieldsPatch }
  | { type: 'SET_ALLOCATIONS'; nodeId: string; allocations: Allocation[] }
  | {
      type: 'BULK_SET_FIELDS';
      ids: ReadonlySet<string>;
      patch: NodeFieldsPatch;
      /** BR-2: contract/billable/assignee patches only apply to tasks. */
      taskOnly?: boolean;
    }
  | { type: 'BULK_INDENT'; ids: readonly string[] }
  | { type: 'BULK_OUTDENT'; ids: readonly string[] }
  | { type: 'TOGGLE_SELECT'; id: string }
  | { type: 'SELECT_RANGE'; ids: readonly string[]; anchorId: string }
  | { type: 'SET_SELECTION'; ids: readonly string[] }
  | { type: 'CLEAR_SELECTION' }
  | {
      type: 'INSERT_NODE';
      node: PlanNode;
      position: InsertPosition;
      /** Insert as child of a (possibly leaf) parent — applies the BR-1 transfer. */
      asChildOf?: string;
      focusTitle?: boolean;
    }
  | {
      /**
       * The timeline's date writes: a different value per node, in one revision.
       * Deliberately narrower than a general multi-node patch — dates cannot
       * violate BR-1 (hours/allocations) or BR-2 (kinds), so this action needs no
       * kind guard and a reviewer can see that at a glance.
       */
      type: 'SET_NODE_DATES';
      updates: readonly {
        id: string;
        startDate?: string;
        endDate?: string;
        targetDate?: string;
      }[];
    }
  | {
      /**
       * Bulk append (the capture composer). One revision for the whole outline,
       * so pasting 50 lines is one autosave, not 50.
       */
      type: 'INSERT_NODES';
      nodes: readonly PlanNode[];
      position: InsertPosition;
    }
  | { type: 'UNFOLD_ALL' }
  /**
   * The contract new tasks are stamped with. Pushed by the provider, which is
   * the only place that can see both the document's default and the contract
   * LIST the one-contract fallback needs. View state: no revision bump, so it
   * never marks the document dirty or triggers a save.
   */
  | { type: 'SET_CONTRACT_PREFILL'; contractId: string | undefined }
  | { type: 'DELETE_NODES'; ids: ReadonlySet<string> }
  | { type: 'DUPLICATE_NODES'; ids: ReadonlySet<string>; makeId: () => string }
  | { type: 'MOVE_SUBTREE'; sourceId: string; target: InsertPosition }
  | { type: 'INDENT_NODE'; id: string }
  | { type: 'OUTDENT_NODE'; id: string }
  | { type: 'SAVE_STARTED' }
  | {
      type: 'SAVE_SUCCEEDED';
      serverDocument: ProjectDocument;
      version: number;
      savedRevision: number;
    }
  | { type: 'SAVE_FAILED' }
  | { type: 'SAVE_CONFLICT' }
  | { type: 'RESOLVE_CONFLICT_KEEP_MINE'; version: number }
  | { type: 'ADOPT_SERVER'; document: ProjectDocument; version: number };

export const createInitialEditorState = (input: {
  projectId: string;
  document: ProjectDocument;
  version: number;
}): EditorState => ({
  projectId: input.projectId,
  document: input.document,
  revision: 0,
  serverVersion: input.version,
  savedRevision: 0,
  saveStatus: 'saved',
  foldedIds: new Set(),
  activeId: null,
  resourcesNodeId: null,
  selectedIds: new Set(),
  selectionAnchorId: null,
  editing: null,
  contractPrefillId: undefined,
});

/** A document mutation: new plan (or document) ⇒ revision bump + dirty. */
const withPlan = (state: EditorState, plan: PlanNode[]): EditorState => {
  if (plan === state.document.plan) return state;
  return {
    ...state,
    document: { ...state.document, plan },
    revision: state.revision + 1,
    saveStatus: state.saveStatus === 'conflict' ? 'conflict' : 'dirty',
  };
};

const findInPlan = (plan: PlanNode[], id: string): PlanNode | undefined => {
  const stack: PlanNode[] = [...plan];
  while (stack.length > 0) {
    const node = stack.pop()!;
    if (node.id === id) return node;
    for (const child of node.children) stack.push(child);
  }
  return undefined;
};

const withoutIds = <T extends ReadonlySet<string>>(set: T, ids: ReadonlySet<string>) => {
  const next = new Set(set);
  for (const id of ids) next.delete(id);
  return next;
};

export const editorReducer = (state: EditorState, action: EditorAction): EditorState => {
  switch (action.type) {
    case 'RESET_FROM_SERVER':
      return createInitialEditorState({
        projectId: action.projectId,
        document: action.document,
        version: action.version,
      });

    case 'TOGGLE_FOLD': {
      const foldedIds = new Set(state.foldedIds);
      if (foldedIds.has(action.id)) {
        foldedIds.delete(action.id);
      } else {
        foldedIds.add(action.id);
      }
      return { ...state, foldedIds };
    }

    case 'SET_ACTIVE':
      return state.activeId === action.id
        ? state
        : // Selection is context for the side panel; an open Resources popover
          // for another node would contradict it (one thing open at a time).
          { ...state, activeId: action.id, resourcesNodeId: null };

    case 'OPEN_RESOURCES':
      return { ...state, resourcesNodeId: action.id, activeId: action.id, editing: null };

    case 'CLOSE_RESOURCES': {
      if (state.resourcesNodeId === null) return state;
      /*
        Nothing to sweep up: a line without a role never reaches the document
        (BR-29) — the overlay's draft lives in its own state and dies with it,
        so the document never holds a resource nobody chose.
      */
      return { ...state, resourcesNodeId: null };
    }

    case 'START_EDITING':
      return {
        ...state,
        editing: {
          nodeId: action.nodeId,
          field: action.field,
          ...(action.openPicker ? { openPicker: true } : {}),
        },
        activeId: action.nodeId,
      };

    case 'STOP_EDITING':
      return state.editing === null ? state : { ...state, editing: null };

    case 'SET_PROJECT_NAME': {
      /*
        The name rides the editor's own autosave rather than a separate PUT:
        `useAutosave` sends the whole document with the last-known version
        (BR-20), so a second writer would race it into a 409.
      */
      if (action.name === state.document.name) return state;
      return {
        ...state,
        document: { ...state.document, name: action.name },
        revision: state.revision + 1,
        saveStatus: state.saveStatus === 'conflict' ? 'conflict' : 'dirty',
      };
    }

    case 'SET_NODE_FIELDS':
      return withPlan(state, updateNodeFields(state.document.plan, action.nodeId, action.patch));

    case 'SET_ALLOCATIONS':
      /*
        Allocating never rewrites the budget (BR-28). This used to clear
        `estimatedHours` on every write, which is why the overlay had to
        capture the budget at mount and why the row could not show the two
        numbers side by side.
      */
      return withPlan(
        state,
        updateNodeFields(state.document.plan, action.nodeId, {
          allocations: action.allocations,
        }),
      );

    case 'BULK_SET_FIELDS': {
      // One rebuild pass for the whole selection. Looping updateNodeFields here
      // re-walked the tree per id — O(n·k), so "select all" on a large plan was
      // quadratic in the node count.
      const patchById = new Map<string, NodeFieldsPatch>();
      for (const id of action.ids) {
        const node = findInPlan(state.document.plan, id);
        if (!node) continue;
        if (action.taskOnly && node.kind !== 'task') continue;
        patchById.set(id, action.patch);
      }
      return withPlan(state, updateManyNodeFields(state.document.plan, patchById));
    }

    case 'BULK_INDENT': {
      let plan = state.document.plan;
      for (const id of action.ids) plan = indentNode(plan, id);
      return withPlan(state, plan);
    }

    case 'BULK_OUTDENT': {
      let plan = state.document.plan;
      for (const id of action.ids) plan = outdentNode(plan, id);
      return withPlan(state, plan);
    }

    case 'TOGGLE_SELECT': {
      const selectedIds = new Set(state.selectedIds);
      if (selectedIds.has(action.id)) {
        selectedIds.delete(action.id);
      } else {
        selectedIds.add(action.id);
      }
      return {
        ...state,
        selectedIds,
        selectionAnchorId: action.id,
        resourcesNodeId: null,
        editing: null,
      };
    }

    case 'SELECT_RANGE': {
      const selectedIds = new Set(state.selectedIds);
      for (const id of action.ids) selectedIds.add(id);
      return { ...state, selectedIds, selectionAnchorId: action.anchorId };
    }

    case 'SET_SELECTION':
      return {
        ...state,
        selectedIds: new Set(action.ids),
        selectionAnchorId: action.ids[action.ids.length - 1] ?? null,
        resourcesNodeId: null,
        editing: null,
      };

    case 'CLEAR_SELECTION':
      return state.selectedIds.size === 0
        ? state
        : { ...state, selectedIds: new Set(), selectionAnchorId: null };

    case 'INSERT_NODE': {
      const plan = action.asChildOf
        ? insertChildWithTransfer(
            state.document.plan,
            action.asChildOf,
            action.position.childIndex,
            action.node,
          )
        : insertNode(state.document.plan, action.position, action.node);
      const next = withPlan(
        state,
        applyContractPrefill(plan, new Set([action.node.id]), state.contractPrefillId),
      );
      if (next === state) return state;
      return {
        ...next,
        activeId: action.node.id,
        editing: action.focusTitle ? { nodeId: action.node.id, field: 'title' } : next.editing,
      };
    }

    case 'SET_NODE_DATES': {
      if (action.updates.length === 0) return state;
      const patchById = new Map<string, NodeFieldsPatch>();
      for (const { id, ...dates } of action.updates) patchById.set(id, dates);
      return withPlan(state, updateManyNodeFields(state.document.plan, patchById));
    }

    case 'INSERT_NODES': {
      if (action.nodes.length === 0) return state;
      let plan = state.document.plan;
      for (const node of action.nodes) plan = insertNode(plan, action.position, node);
      const next = withPlan(
        state,
        applyContractPrefill(plan, subtreeIds(action.nodes), state.contractPrefillId),
      );
      if (next === state) return state;
      // Select the first captured node so the side panel lands on it, and stop
      // any inline edit — the user's attention is on the new subtree, not a cell.
      return { ...next, activeId: action.nodes[0]!.id, editing: null };
    }

    case 'SET_CONTRACT_PREFILL':
      return state.contractPrefillId === action.contractId
        ? state
        : { ...state, contractPrefillId: action.contractId };

    case 'UNFOLD_ALL':
      // View state only: no revision bump, so this never triggers a save.
      return state.foldedIds.size === 0 ? state : { ...state, foldedIds: new Set() };

    case 'DELETE_NODES': {
      const next = withPlan(state, removeSubtrees(state.document.plan, action.ids));
      if (next === state) return state;
      return {
        ...next,
        selectedIds: withoutIds(state.selectedIds, action.ids),
        activeId: state.activeId && action.ids.has(state.activeId) ? null : state.activeId,
        resourcesNodeId:
          state.resourcesNodeId && action.ids.has(state.resourcesNodeId)
            ? null
            : state.resourcesNodeId,
        editing: state.editing && action.ids.has(state.editing.nodeId) ? null : state.editing,
      };
    }

    case 'DUPLICATE_NODES':
      return withPlan(state, duplicateSubtrees(state.document.plan, action.ids, action.makeId));

    case 'MOVE_SUBTREE':
      return withPlan(state, moveSubtree(state.document.plan, action.sourceId, action.target));

    case 'INDENT_NODE':
      return withPlan(state, indentNode(state.document.plan, action.id));

    case 'OUTDENT_NODE':
      return withPlan(state, outdentNode(state.document.plan, action.id));

    case 'SAVE_STARTED':
      return { ...state, saveStatus: 'saving' };

    case 'SAVE_SUCCEEDED': {
      const document = adoptServerCodes(state.document, action.serverDocument);
      return {
        ...state,
        document,
        serverVersion: action.version,
        savedRevision: action.savedRevision,
        saveStatus: state.revision === action.savedRevision ? 'saved' : 'dirty',
      };
    }

    case 'SAVE_FAILED':
      return { ...state, saveStatus: 'error' };

    case 'SAVE_CONFLICT':
      return { ...state, saveStatus: 'conflict' };

    case 'RESOLVE_CONFLICT_KEEP_MINE':
      // Adopt the fresh version, mark dirty — the autosave loop re-PUTs ours.
      return { ...state, serverVersion: action.version, saveStatus: 'dirty' };

    case 'ADOPT_SERVER':
      return {
        ...createInitialEditorState({
          projectId: state.projectId,
          document: action.document,
          version: action.version,
        }),
        foldedIds: state.foldedIds,
        activeId: state.activeId,
        // Contracts did not change because the server sent a document; losing
        // this would leave the next task unmapped until the provider's effect
        // happened to re-fire.
        contractPrefillId: state.contractPrefillId,
      };

    default:
      return state;
  }
};
